/**
 * ChangePasswordCard — self-service credential rotation for the logged-in user.
 * @file src/web/features/admin/components/ChangePasswordCard.tsx
 *
 * Posts to /api/auth/change-password (authenticated), which re-verifies the
 * CURRENT password server-side before setting the new one — the client checks
 * here are only UX. adminFetch carries the Bearer token, so the server knows
 * who to update; no user id travels in the body.
 *
 * Shared by /admin/settings (professional/entity admins) and /platform/settings
 * (platform operators), so both consoles get the same password UI. Keep it
 * console-agnostic — no assumptions about surrounding chrome.
 *
 * @llm-rule WHEN: A console needs a "change my password" panel
 * @llm-rule AVOID: Duplicating the fetch/validation — reuse this card
 */

import { useState, type ChangeEvent } from 'react';
import { PasswordInput, toast } from '@bloomneo/uikit';
import { KeyRound, Loader2 } from 'lucide-react';
import { adminFetch } from '../lib/admin-api';

function PwField({
  label,
  value,
  onChange,
  placeholder,
  autoComplete,
  help,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  autoComplete?: string;
  help?: string;
}) {
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-medium text-ink-700">{label}</label>
      <PasswordInput
        value={value}
        placeholder={placeholder}
        autoComplete={autoComplete}
        onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
      />
      {help && <p className="text-xs text-muted-foreground">{help}</p>}
    </div>
  );
}

export function ChangePasswordCard() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);

  const mismatch = confirm.length > 0 && confirm !== next;
  const tooShort = next.length > 0 && next.length < 8;
  const sameAsOld = next.length > 0 && next === current;
  const canSubmit =
    current.length > 0 && next.length >= 8 && confirm === next && !sameAsOld && !saving;

  function reset() {
    setCurrent('');
    setNext('');
    setConfirm('');
  }

  async function submit() {
    if (!canSubmit) return;
    setSaving(true);
    try {
      const res = await adminFetch('/api/auth/change-password', {
        method: 'POST',
        body: JSON.stringify({ currentPassword: current, newPassword: next }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body?.message || body?.error || `Failed (${res.status})`);
      }
      toast.success('Password changed');
      reset();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not change password');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="rounded-2xl border border-ink-200 bg-white p-6">
      <div className="mb-5">
        <h3 className="text-base font-semibold text-ink-900">Change password</h3>
        <p className="mt-1 text-sm text-ink-500">Update the password for your login. You'll need your current password to confirm.</p>
      </div>

      <div className="rounded-xl border border-ink-200 p-4">
        <p className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-ink-400">
          <KeyRound className="h-3.5 w-3.5" /> Credentials
        </p>
        <div className="grid gap-4 md:grid-cols-2">
          <PwField
            label="Current password"
            value={current}
            onChange={setCurrent}
            placeholder="Enter current password"
            autoComplete="current-password"
          />
          <div className="hidden md:block" />
          <PwField
            label="New password"
            value={next}
            onChange={setNext}
            placeholder="At least 8 characters"
            autoComplete="new-password"
            help={tooShort ? 'Must be at least 8 characters' : sameAsOld ? 'Choose a password different from the current one' : undefined}
          />
          <PwField
            label="Confirm new password"
            value={confirm}
            onChange={setConfirm}
            placeholder="Re-enter new password"
            autoComplete="new-password"
            help={mismatch ? 'Passwords do not match' : undefined}
          />
        </div>
      </div>

      <div className="mt-6 flex items-center justify-end gap-2 border-t border-ink-100 pt-4">
        <button onClick={reset} disabled={saving || (!current && !next && !confirm)}
          className="rounded-full border border-ink-300 bg-white px-5 py-2.5 text-sm font-semibold text-ink-700 transition hover:bg-ink-50 disabled:cursor-not-allowed disabled:opacity-50">Clear</button>
        <button onClick={submit} disabled={!canSubmit}
          className="inline-flex items-center gap-2 rounded-full bg-inkblack px-5 py-2.5 text-sm font-semibold text-white transition hover:-translate-y-0.5 disabled:translate-y-0 disabled:opacity-60">
          {saving && <Loader2 className="h-4 w-4 animate-spin" />} Update password
        </button>
      </div>
    </div>
  );
}
